import { FetchError } from '@commercetools/frontend-sdk';
import { AlertCode, PSP } from 'shared/types/checkout';
import { SDKError } from 'shared/types/shared';

const getAlertCodeForPSP = <T extends PSP>(
  error: SDKError | FetchError,
  psp: T,
) => {
  switch (psp) {
    case PSP.STRIPE:
      switch (error.type) {
        case 'StripeCardError':
          return AlertCode.TRANSACTION_DECLINED;
        default:
          return AlertCode.PAYMENT_PROCESSING_FAILED;
      }
    case PSP.PAYPAL:
      switch (error.code) {
        case 'paypal_api_error':
          return AlertCode.PAYPAL_PAYMENT_PROCESSING_FAILED;
        default:
          return AlertCode.PAYMENT_PROCESSING_FAILED;
      }
    default:
      return AlertCode.PAYMENT_PROCESSING_FAILED;
  }
};

// Helper function to map general error codes to AlertCode
export const getAlertCodeFromError = (
  error: SDKError | FetchError,
): AlertCode => {
  switch (error.code) {
    case 'cart_changed_error':
      return AlertCode.CART_CHANGED;
    case 'product_unavailable_error':
      return AlertCode.PRODUCT_UNAVAILABLE;
    case 'order_already_placed':
      return AlertCode.ORDER_ALREADY_PLACED;
    case 'product_unavailable_to_buy':
      return AlertCode.PRODUCT_UNAVAILABLE_TO_BUY;
    default:
      return AlertCode.UNKNOWN_ERROR; // Fallback for unknown errors
  }
};

// Main function that incorporates both general and PSP-specific error mapping
export const getAlertCodeForError = <T extends PSP>(
  error: SDKError | FetchError,
  psp: T,
): AlertCode => {
  const generalAlertCode = getAlertCodeFromError(error);

  // If a general alert code is found, return it; otherwise, check PSP-specific errors
  return generalAlertCode !== AlertCode.UNKNOWN_ERROR
    ? generalAlertCode
    : getAlertCodeForPSP(error, psp);
};
